import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../../context/authContext";
import { SocketContext } from "../../../context/socketContext"; 
import { makeRequest } from "../../../utils/axios.js"; 
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export const useRightbar = () => {
  const { currentUser } = useContext(AuthContext);
  const { onlineUsers } = useContext(SocketContext);
  const queryClient = useQueryClient();
  const [friends, setFriends] = useState([]);
  const [onlineFriends, setOnlineFriends] = useState([]);
  const [activities, setActivities] = useState([]);

  const { isLoading: suggestionsLoading, data: suggestions } = useQuery({
    queryKey: ["suggestions"],
    queryFn: () => makeRequest.get("/users/suggestions").then((res) => res.data),
  });

  const addFriendMutation = useMutation({
    mutationFn: (userId) => makeRequest.post("/friendships", { userId }),
    onSuccess: (_, userId) => {
      queryClient.setQueryData(["suggestions"], (oldData) => {
        if (!oldData) return [];
        return oldData.filter((user) => user.id !== userId);
      });
      queryClient.invalidateQueries({ queryKey: ["friendship", userId] });
      queryClient.invalidateQueries({ queryKey: ["friendRequests"] });
    },
  });

  const handleDeleteSuggestion = (userId) => {
    queryClient.setQueryData(["suggestions"], (oldData) => {
      if (!oldData) return [];
      return oldData.filter((user) => user.id !== userId);
    });
  };

  useEffect(() => {
    const getFriends = async () => {
      try {
        if (currentUser?.id) {
          const res = await makeRequest.get("/friendships/friends?userId=" + currentUser.id);
          setFriends(res.data);
        }
      } catch (err) {
        console.log(err);
      }
    };
    getFriends();
  }, [currentUser]);

  useEffect(() => {
    if (!friends.length || !onlineUsers) return;
    const onlineFriendsList = friends.filter((f) => onlineUsers.includes(f.id));
    setOnlineFriends(onlineFriendsList);
  }, [onlineUsers, friends]);
  
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const res = await makeRequest.get("/notifications?limit=5"); 
        setActivities(res.data.data || res.data); 
      } catch (err) {
        console.log("Error when downloading Latest Activities", err);
      }
    };

    if (currentUser) {
      fetchActivities();
    }
  }, [currentUser]);

  return {
    suggestionsLoading,
    suggestions,
    isAddingFriend: addFriendMutation.isPending,
    onAddFriend: (userId) => addFriendMutation.mutate(userId),
    onDeleteSuggestion: handleDeleteSuggestion,
    activities,
    onlineFriends
  };
};